/**
 * Workspace Icons
 *
 * Icons for workspace items (folders, tables, linked tables).
 * Uses Lucide icons with consistent styling and colors.
 *
 * @module components/icons/WorkspaceIcons
 */

import { Folder, Table, Link2 } from 'lucide-react';
import { cn } from '@loader/ui';
import type { WorkspaceItemType } from '@loader/shared-types';

// ============================================================================
// TYPES
// ============================================================================

interface IconProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
  sm: 'size-6',
  md: 'size-8',
  lg: 'size-10',
  xl: 'size-12',
};

// ============================================================================
// FOLDER ICON
// ============================================================================

/**
 * Folder icon with amber color
 */
export function FolderIcon({ className, size = 'md' }: IconProps) {
  return (
    <Folder
      className={cn(
        sizeClasses[size],
        'fill-amber-500 text-amber-600 dark:fill-amber-400 dark:text-amber-500',
        className
      )}
    />
  );
}

// ============================================================================
// TABLE ICON
// ============================================================================

/**
 * Table icon with blue color
 */
export function TableIcon({ className, size = 'md' }: IconProps) {
  return (
    <Table
      className={cn(
        sizeClasses[size],
        'text-blue-600 dark:text-blue-400',
        className
      )}
    />
  );
}

// ============================================================================
// LINKED TABLE ICON
// ============================================================================

/**
 * Linked table icon with purple color
 */
export function LinkedTableIcon({ className, size = 'md' }: IconProps) {
  return (
    <Link2
      className={cn(
        sizeClasses[size],
        'text-purple-600 dark:text-purple-400',
        className
      )}
    />
  );
}

// ============================================================================
// UNIFIED WORKSPACE ICON
// ============================================================================

interface WorkspaceIconProps extends IconProps {
  type: WorkspaceItemType;
}

/**
 * Unified workspace icon component that renders the appropriate icon based on type
 */
export function WorkspaceIcon({ type, className, size = 'md' }: WorkspaceIconProps) {
  switch (type) {
    case 'folder':
      return <FolderIcon className={className} size={size} />;
    case 'table':
      return <TableIcon className={className} size={size} />;
    case 'linked_table':
      return <LinkedTableIcon className={className} size={size} />;
    default:
      return <TableIcon className={className} size={size} />;
  }
}

// ============================================================================
// SIMPLE ICON VARIANT (for smaller contexts like sidebars)
// ============================================================================

interface SimpleIconProps {
  type: WorkspaceItemType;
  className?: string;
}

/**
 * Simple colored icon for use in sidebars, lists, etc.
 */
export function SimpleWorkspaceIcon({ type, className }: SimpleIconProps) {
  const baseClass = cn('size-4', className);

  switch (type) {
    case 'folder':
      return (
        <Folder
          className={cn(
            baseClass,
            'fill-amber-500 text-amber-600 dark:fill-amber-400 dark:text-amber-500'
          )}
        />
      );
    case 'table':
      return (
        <Table
          className={cn(baseClass, 'text-blue-600 dark:text-blue-400')}
        />
      );
    case 'linked_table':
      return (
        <Link2
          className={cn(baseClass, 'text-purple-600 dark:text-purple-400')}
        />
      );
    default:
      return (
        <Table
          className={cn(baseClass, 'text-gray-500 dark:text-gray-400')}
        />
      );
  }
}
